import { useEffect, useState } from 'react'
import { Button } from '@/components/nexus/primitives/button'
import { hasAdTrackingConsent, requestAdTrackingConsent } from '@/lib/att'
import {
  applyIosLaunchConsent,
  type ConsentChoice,
  readConsentChoice,
  storeConsentChoice,
} from '@/lib/consent'
import { updateConsent } from '@/lib/gtm'
import { optInPostHog, optOutPostHog } from '@/lib/posthog'
import { isCapacitorIOS } from '@/lib/runtime'

/**
 * Unified cookie/analytics consent banner (web + Android). Drives GTM Consent
 * Mode and PostHog opt-in/out from one choice. The *default* (denied) is set
 * inline in `index.html` before `gtm.js` runs and via
 * `opt_out_capturing_by_default` on PostHog — this component only records and
 * re-applies the user's decision.
 *
 * On the native iOS shell the banner never renders: App Store Guideline
 * 5.1.2(i) requires the ATT dialog to be the (observable) consent surface, so
 * consent is applied by `applyIosLaunchConsent` at every launch instead —
 * analytics on, `ad_*` following the ATT answer. See `lib/consent.ts`.
 *
 * Distinct from the OAuth2 `/consent` page (that authorizes API clients).
 */
const OPEN_EVENT = 'stopal:open-consent'

/**
 * Re-open the consent banner so the user can change a prior choice (GDPR
 * withdraw). Call from anywhere (e.g. a footer "Cookie settings" link).
 * No-op on the iOS shell, where there is no banner — ad consent is managed
 * via the system ATT setting (Settings → Privacy & Security → Tracking).
 */
export function openConsentSettings(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(OPEN_EVENT))
}

/**
 * Apply a consent choice. `ads` is decided separately from `analytics` because
 * advertising signals are gated behind App Tracking Transparency on native iOS
 * (see `att.ts`); on web/Android `ads` tracks `analytics`. PostHog is
 * first-party analytics, so it follows `analytics`, not `ads`.
 *
 * `requestAds` chooses how the ad gate is resolved: on the user's explicit
 * Accept we *prompt* for ATT; when silently re-applying a stored choice on
 * launch we only *read* the current status (no prompt).
 */
async function applyConsent(
  granted: boolean,
  requestAds: () => Promise<boolean>,
): Promise<void> {
  const ads = granted ? await requestAds() : false
  updateConsent({ analytics: granted, ads })
  if (granted) {
    optInPostHog()
  } else {
    optOutPostHog()
  }
}

export function ConsentBanner() {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (isCapacitorIOS()) {
      // iOS shell: no cookie banner — ATT at launch is the only consent
      // surface. Runs every launch so a Settings-level ATT revocation is
      // re-applied (the dialog itself only shows while notDetermined).
      void applyIosLaunchConsent(requestAdTrackingConsent, optInPostHog)
      return
    }
    const stored = readConsentChoice()
    if (stored) {
      // Re-apply without prompting — read the existing ATT status only.
      void applyConsent(stored === 'granted', hasAdTrackingConsent)
    } else {
      setVisible(true)
    }
    const reopen = () => setVisible(true)
    window.addEventListener(OPEN_EVENT, reopen)
    return () => window.removeEventListener(OPEN_EVENT, reopen)
  }, [])

  function decide(choice: ConsentChoice) {
    storeConsentChoice(choice)
    // On explicit Accept, prompt for ATT before granting advertising signals.
    void applyConsent(choice === 'granted', requestAdTrackingConsent)
    setVisible(false)
  }

  if (!visible) return null

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className="fixed bottom-4 right-4 z-50 w-[min(20rem,calc(100vw-2rem))] rounded-lg border border-border-default bg-surface-card p-4 shadow-lg"
    >
      <p className="text-sm text-text-secondary">
        We use cookies for analytics and advertising. See our{' '}
        <a href="/privacy" className="underline hover:text-text-primary">
          Privacy Policy
        </a>
        .
      </p>
      <div className="mt-3 flex justify-end gap-2">
        <Button variant="ghost" size="small" onClick={() => decide('denied')}>
          Decline
        </Button>
        <Button size="small" onClick={() => decide('granted')}>
          Accept
        </Button>
      </div>
    </div>
  )
}
